import { ZuruflexApiError, type PromotionInput } from '@zuruflex/api-client';
import {
  minorUnitsPerMajor,
  type CurrencyCode,
  type Promotion,
  type PromotionAudience,
  type PromotionDiscountType,
  type PromotionTrigger,
  type Vehicle,
} from '@zuruflex/types';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { endsAtAfter, lastDayOf } from '../lib/promotions.js';
import { Choice } from './car-fields.js';

export interface PromotionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The promo being edited, or absent for a new one. */
  promotion?: Promotion;
  onSaved?: (promotion: Promotion) => void;
}

/** Typed in major units, held in minor ones — the same rule as a car's rate. */
function toMajor(amount: number | null, currency: string): string {
  return amount === null ? '' : String(amount / minorUnitsPerMajor(currency));
}

function toMinor(value: string, currency: string): number | null {
  return value.trim() === '' ? null : Math.round(Number(value) * minorUnitsPerMajor(currency));
}

/** A blank box is "no limit", which the API spells null. */
function wholeOrNull(value: string): number | null {
  return value.trim() === '' ? null : Math.round(Number(value));
}

function startOfDay(day: Date): Date {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Starting or changing one of this partner's promos.
 *
 * The shape of the admin's dialog with the Zuruflex-only parts taken out: no
 * funder, no partner picker, no assigned audience. The API forces a
 * partner's promo onto their own fleet at their own cost whatever the body
 * says; this form simply never offers the choices it would overrule, and
 * says once, near the button, who pays.
 *
 * The cars are this partner's own, filtered to the promo's currency — a
 * cedi discount on a car priced in francs could never apply, and ticking it
 * would only look as though it did.
 */
export function PromotionDialog({ open, onOpenChange, promotion, onSaved }: PromotionDialogProps) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();
  const queryClient = useQueryClient();
  const editing = Boolean(promotion);

  const { data: fleet } = useQuery({
    queryKey: ['my-fleet'],
    queryFn: () => api.listVehicles({ partnerId: partner.id, perPage: 100 }),
    enabled: open,
  });
  const cars: Vehicle[] = useMemo(() => fleet?.items ?? [], [fleet]);

  const [name, setName] = useState(promotion?.name ?? '');
  const [description, setDescription] = useState(promotion?.description ?? '');
  const [trigger, setTrigger] = useState<PromotionTrigger>(promotion?.trigger ?? 'CODE');
  const [code, setCode] = useState(promotion?.code ?? '');
  const [audience, setAudience] = useState<PromotionAudience>(
    promotion?.audience === 'FIRST_HIRE' ? 'FIRST_HIRE' : 'EVERYONE',
  );
  const [discountType, setDiscountType] = useState<PromotionDiscountType>(
    promotion?.discountType ?? 'PERCENT',
  );
  const [currency, setCurrency] = useState<CurrencyCode>(promotion?.currency ?? partner.currency);
  const [percent, setPercent] = useState(
    promotion?.percentBps ? String(promotion.percentBps / 100) : '',
  );
  const [amountOff, setAmountOff] = useState(toMajor(promotion?.amountOff ?? null, currency));
  const [maxDiscount, setMaxDiscount] = useState(toMajor(promotion?.maxDiscount ?? null, currency));
  const [startDay, setStartDay] = useState<Date | undefined>(
    promotion ? new Date(promotion.startsAt) : undefined,
  );
  const originalStart = promotion ? startOfDay(new Date(promotion.startsAt)).getTime() : null;
  const startMoved = !startDay || startOfDay(startDay).getTime() !== originalStart;
  const [lastDay, setLastDay] = useState<Date | undefined>(
    promotion?.endsAt ? lastDayOf(promotion.endsAt) : undefined,
  );
  const [maxRedemptions, setMaxRedemptions] = useState(
    promotion?.maxRedemptions ? String(promotion.maxRedemptions) : '',
  );
  const [maxPerCustomer, setMaxPerCustomer] = useState(String(promotion?.maxPerCustomer ?? 1));
  const [minDays, setMinDays] = useState(promotion?.minDays ? String(promotion.minDays) : '');
  const [minSubtotal, setMinSubtotal] = useState(toMajor(promotion?.minSubtotal ?? null, currency));
  const [vehicleIds, setVehicleIds] = useState<string[]>(promotion?.vehicleIds ?? []);
  const [error, setError] = useState<string | null>(null);

  /*
   * The partner's own currency, plus any a car of theirs is priced in. A
   * partner with one country — nearly all of them — never sees the choice.
   */
  const currencies = useMemo(
    () => [...new Set<CurrencyCode>([partner.currency, ...cars.map((car) => car.currency)])],
    [partner.currency, cars],
  );
  const eligibleCars = cars.filter((car) => car.currency === currency);

  const toggleCar = (id: string, on: boolean) =>
    setVehicleIds((current) =>
      on ? [...new Set([...current, id])] : current.filter((value) => value !== id),
    );

  const body = (): PromotionInput => {
    const shared = {
      name: name.trim(),
      description: description.trim() || undefined,
      audience,
      discountType,
      percentBps: discountType === 'PERCENT' ? Math.round(Number(percent) * 100) : undefined,
      amountOff: discountType === 'FIXED' ? (toMinor(amountOff, currency) ?? undefined) : undefined,
      currency,
      endsAt: lastDay ? endsAtAfter(lastDay) : null,
      maxPerCustomer: Math.max(1, Math.round(Number(maxPerCustomer) || 1)),
      // Only the cars that can apply: switching currency leaves the others
      // ticked in state, and they should not reach the API. Until the fleet
      // has loaded there is nothing to filter against, and filtering would
      // quietly widen an edited promo to every car.
      vehicleIds: fleet
        ? vehicleIds.filter((id) => eligibleCars.some((car) => car.id === id))
        : vehicleIds,
    };
    return {
      ...shared,
      ...(trigger === 'CODE' ? { code: code.trim().toUpperCase() } : {}),
      // Sent only when the day was picked or moved. Re-sending an unchanged
      // start would snap a promo that began at 14:10 back to midnight.
      ...(startDay && startMoved ? { startsAt: startOfDay(startDay).toISOString() } : {}),
    };
  };

  /**
   * The limits go as nulls when their boxes are empty: on a PATCH an absent
   * key means "leave it", and a cleared box means "no limit any more".
   */
  const limits = () => ({
    maxDiscount: discountType === 'PERCENT' ? toMinor(maxDiscount, currency) : null,
    maxRedemptions: wholeOrNull(maxRedemptions),
    minDays: wholeOrNull(minDays),
    minSubtotal: toMinor(minSubtotal, currency),
  });

  const save = useMutation({
    mutationFn: () =>
      promotion
        ? api.updatePromotion(promotion.id, {
            ...body(),
            description: description.trim() || null,
            ...limits(),
          })
        : api.createPromotion({ ...body(), trigger, ...limits() }),
    onMutate: () => setError(null),
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async (saved) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['my-promotions'] }),
        queryClient.invalidateQueries({ queryKey: ['promotion', saved.id] }),
      ]);
      onSaved?.(saved);
      onOpenChange(false);
    },
  });

  const valueOk =
    discountType === 'PERCENT'
      ? Number(percent) > 0 && Number(percent) <= 100
      : Number(amountOff) > 0;
  const ready =
    name.trim().length >= 2 &&
    (trigger === 'AUTOMATIC' || /^[A-Za-z0-9-]{3,32}$/.test(code.trim())) &&
    valueOk &&
    Number(maxPerCustomer) >= 1 &&
    (!lastDay || !startDay || startOfDay(lastDay) >= startOfDay(startDay));

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('promotions.form.titleEdit') : t('promotions.form.titleNew')}
          </DialogTitle>
          <DialogDescription>{t('promotions.subtitle')}</DialogDescription>
        </DialogHeader>

        <form id="promotion-form" onSubmit={submit} className="space-y-5">
          <FormField
            label={t('promotions.form.name')}
            hint={t('promotions.form.nameHint')}
            required
          >
            {(field) => (
              <Input
                {...field}
                value={name}
                maxLength={120}
                required
                autoFocus
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </FormField>

          {/* Fixed once it exists: the API refuses to turn a code into an
              automatic promo, so the control would only offer an error. */}
          {editing ? (
            <p className="text-xs text-text-muted">
              {t('promotions.form.trigger')}:{' '}
              <span className="font-medium text-text">
                {t(`promotions.form.trigger${trigger}`)}
              </span>{' '}
              · {t('promotions.form.triggerLocked')}
            </p>
          ) : (
            <div className="space-y-1.5">
              <Choice<PromotionTrigger>
                label={t('promotions.form.trigger')}
                options={['CODE', 'AUTOMATIC']}
                value={trigger}
                onChange={setTrigger}
                render={(option) => t(`promotions.form.trigger${option}`)}
              />
              <p className="text-xs text-text-muted">{t('promotions.form.triggerHint')}</p>
            </div>
          )}

          {trigger === 'CODE' ? (
            <FormField
              label={t('promotions.form.code')}
              hint={t('promotions.form.codeHint')}
              required
            >
              {(field) => (
                <Input
                  {...field}
                  value={code}
                  maxLength={32}
                  required
                  placeholder={t('promotions.form.codePlaceholder')}
                  className="font-mono uppercase"
                  onChange={(event) => setCode(event.target.value.toUpperCase())}
                />
              )}
            </FormField>
          ) : null}

          <Choice<PromotionAudience>
            label={t('promotions.form.audience')}
            options={['EVERYONE', 'FIRST_HIRE']}
            value={audience}
            onChange={setAudience}
            render={(option) => t(`promotions.form.audience${option}`)}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Choice<PromotionDiscountType>
              label={t('promotions.form.discountType')}
              options={['PERCENT', 'FIXED']}
              value={discountType}
              onChange={setDiscountType}
              render={(option) => t(`promotions.form.discountType${option}`)}
            />
            {currencies.length > 1 ? (
              <Choice<CurrencyCode>
                label={t('promotions.form.currency')}
                options={currencies}
                value={currency}
                onChange={setCurrency}
                render={(option) => option}
              />
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {discountType === 'PERCENT' ? (
              <>
                <FormField label={t('promotions.form.percent')} required>
                  {(field) => (
                    <Input
                      {...field}
                      type="number"
                      min={0.01}
                      max={100}
                      step="0.01"
                      value={percent}
                      required
                      onChange={(event) => setPercent(event.target.value)}
                    />
                  )}
                </FormField>
                <FormField
                  label={t('promotions.form.maxDiscount', { currency })}
                  hint={t('promotions.form.maxDiscountHint')}
                >
                  {(field) => (
                    <Input
                      {...field}
                      type="number"
                      min={1}
                      step="1"
                      value={maxDiscount}
                      onChange={(event) => setMaxDiscount(event.target.value)}
                    />
                  )}
                </FormField>
              </>
            ) : (
              <FormField label={t('promotions.form.amount', { currency })} required>
                {(field) => (
                  <Input
                    {...field}
                    type="number"
                    min={1}
                    step="1"
                    value={amountOff}
                    required
                    onChange={(event) => setAmountOff(event.target.value)}
                  />
                )}
              </FormField>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('promotions.form.startsAt')}>
              {(field) => (
                <DatePicker
                  id={field.id}
                  value={startDay}
                  onChange={setStartDay}
                  placeholder={t('promotions.form.pickDate')}
                  className="w-full"
                />
              )}
            </FormField>
            <FormField label={t('promotions.form.endsAt')} hint={t('promotions.form.endsAtHint')}>
              {(field) => (
                <div className="flex gap-2">
                  <DatePicker
                    id={field.id}
                    value={lastDay}
                    onChange={setLastDay}
                    disablePast
                    placeholder={t('promotions.form.pickDate')}
                    className="w-full"
                  />
                  {lastDay ? (
                    <Button type="button" variant="ghost" onClick={() => setLastDay(undefined)}>
                      {t('common:action.clear')}
                    </Button>
                  ) : null}
                </div>
              )}
            </FormField>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              label={t('promotions.form.maxRedemptions')}
              hint={t('promotions.form.maxRedemptionsHint')}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step="1"
                  value={maxRedemptions}
                  onChange={(event) => setMaxRedemptions(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('promotions.form.maxPerCustomer')} required>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  max={100}
                  step="1"
                  value={maxPerCustomer}
                  required
                  onChange={(event) => setMaxPerCustomer(event.target.value)}
                />
              )}
            </FormField>
            <FormField label={t('promotions.form.minDays')} hint={t('promotions.form.optional')}>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step="1"
                  value={minDays}
                  onChange={(event) => setMinDays(event.target.value)}
                />
              )}
            </FormField>
            <FormField
              label={t('promotions.form.minSubtotal', { currency })}
              hint={t('promotions.form.optional')}
            >
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={1}
                  step="1"
                  value={minSubtotal}
                  onChange={(event) => setMinSubtotal(event.target.value)}
                />
              )}
            </FormField>
          </div>

          <fieldset>
            <legend className="mb-1 text-sm font-medium text-text">
              {t('promotions.form.cars')}
            </legend>
            <p className="mb-2 text-xs text-text-muted">{t('promotions.form.carsHint')}</p>
            {eligibleCars.length === 0 ? (
              <p className="text-sm text-text-muted">{t('promotions.app.noCars')}</p>
            ) : (
              <ul className="grid max-h-56 gap-1 overflow-y-auto rounded-card border border-border-default p-2 sm:grid-cols-2">
                {eligibleCars.map((car) => {
                  const id = `promo-car-${car.id}`;
                  return (
                    <li key={car.id}>
                      <label
                        htmlFor={id}
                        className="flex cursor-pointer items-center gap-2 rounded-field px-2 py-1.5 text-sm hover:bg-surface-hover"
                      >
                        <Checkbox
                          id={id}
                          checked={vehicleIds.includes(car.id)}
                          onCheckedChange={(next) => toggleCar(car.id, next === true)}
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {car.make} {car.model}
                        </span>
                        <span className="font-mono text-xs text-text-muted">{car.plateNumber}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <FormField
            label={t('promotions.form.description')}
            hint={t('promotions.form.descriptionHint')}
          >
            {(field) => (
              <Textarea
                {...field}
                rows={2}
                maxLength={1000}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </FormField>

          <Alert variant="info">{t('promotions.form.funded')}</Alert>

          {error ? <Alert variant="danger">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common:action.cancel')}
          </Button>
          <Button type="submit" form="promotion-form" disabled={!ready} isLoading={save.isPending}>
            {editing ? t('promotions.form.save') : t('promotions.form.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
